import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import PublicNav from "../../components/layout/PublicNav";
import PublicFooter from "../../components/layout/PublicFooter";
import SMSRecruiterCard, { SMS_COPY } from "../../components/SMSRecruiterCard";
import PageMeta from "../../components/seo/PageMeta";
import EasyApplyModal from "../../components/EasyApplyModal";
import { CEIPAL_SUBMIT_RESUME_URL, RECRUITER_EMAIL } from "../../data/recruitingLinks";
import {
  ArrowRight, ArrowUpRight, Sparkles, MapPin, Briefcase, Heart,
  Globe2, Zap, ShieldCheck, Coffee, Users, Stethoscope, Layers,
  Building2, GraduationCap, Plane, Linkedin, Send,
} from "lucide-react";

/* ---------------- Data ---------------- */

const VALUES = [
  { Icon: Stethoscope, t: "Clinical first",
    d: "Every product decision begins with the floor. We don't ship features our users wouldn't defend to their charge nurse." },
  { Icon: ShieldCheck, t: "Compliance as a default",
    d: "Audit trails, primary-source verification, and HR.01/HR.02 alignment aren't add-ons — they're the minimum table stakes." },
  { Icon: Zap, t: "Operate like operators",
    d: "We ship what solves a specific constraint this week. We decline the features that don't. No roadmap theatre." },
  { Icon: Heart, t: "Tell the truth early",
    d: "Bad news — a no-show, a credential lapse, a missed SLA — goes to the customer the hour we know, not the quarter we review." },
];

const BENEFITS = [
  { Icon: Coffee,    t: "Remote-first, globally distributed",
    d: "Work from Missouri City, Stafford, Noida, or wherever you do your best thinking. Quarterly team off-sites." },
  { Icon: Heart,     t: "Full health + wellbeing coverage",
    d: "Medical, dental, vision for you and dependents. Mental-health stipend. 4 weeks paid time off, plus federal holidays." },
  { Icon: Plane,     t: "Paid parental + caregiver leave",
    d: "16 weeks fully-paid parental leave (birthing & non-birthing parents). Elder-caregiver leave on request." },
  { Icon: GraduationCap, t: "$2,500 learning stipend",
    d: "Certifications, courses, conferences — whatever keeps your craft sharp. No approval committee." },
  { Icon: Layers,    t: "Equity from day one",
    d: "Meaningful ownership for every full-time role. Vesting aligned with long-horizon outcomes, not annual resets." },
  { Icon: Globe2,    t: "24/7 recruiter buddy",
    d: "For all roles outside recruiting — you're paired with a recruiter buddy from day one to stay close to customer reality." },
];

const ROLES = [
  { t: "Senior Full-Stack Engineer",        l: "Remote · USA / India", tag: "Engineering",  team: "Platform" },
  { t: "Staff ML Engineer, Matching",        l: "Remote · USA",         tag: "Engineering",  team: "ML" },
  { t: "Clinical Product Manager",           l: "Dallas, TX · Hybrid",  tag: "Product",      team: "Clinician" },
  { t: "Director, Credentialing Operations", l: "Remote · USA",         tag: "Operations",   team: "Clinical" },
  { t: "Senior Recruiter — ICU / ER",        l: "Remote · USA",         tag: "Recruiting",   team: "Clinical" },
  { t: "Enterprise Account Executive",       l: "New York, NY",         tag: "Sales",        team: "GTM" },
  { t: "Growth Marketing Lead",              l: "Remote · USA",         tag: "Marketing",    team: "GTM" },
  { t: "Customer Success Manager, MSPs",     l: "Houston, TX · Hybrid", tag: "Success",      team: "GTM" },
  { t: "Senior Compliance Analyst",          l: "Stafford, TX · Hybrid",tag: "Compliance",   team: "Clinical" },
];

const PROCESS = [
  { n: "01", t: "Apply", d: "30 seconds. Resume + a note on what you're trying to solve. No cover letters theatre." },
  { n: "02", t: "Recruiter chat", d: "20 min with our talent team. We share the role, you share what would make this job great." },
  { n: "03", t: "Craft conversation", d: "45–60 min with the hiring manager. Real work, real examples — no brainteasers." },
  { n: "04", t: "Team loop", d: "2–3 conversations with cross-functional teammates. We pay for your time (gift card) on the spot." },
  { n: "05", t: "Offer + equity review", d: "One detailed offer walk-through. Compensation transparency — no multi-round haggle." },
];

const LOCATIONS = [
  { label: "USA · Head Office",   lines: ["5423 Pecan Fall PL", "Missouri City, TX 77459"] },
  { label: "USA · Corporate",     lines: ["11104 West Airport Blvd, Suite 213", "Stafford, TX 77477"] },
  { label: "India · Operations",  lines: ["First floor, A-83, A Block, Sector 63", "Noida, UP 201309"] },
];

/* ---------------- Page ---------------- */

export default function Careers() {
  const [selectedRole, setSelectedRole] = useState(null);
  const [applyOpen, setApplyOpen] = useState(false);

  const openApply = (role) => {
    setSelectedRole(role);
    setApplyOpen(true);
  };

  return (
    <div className="min-h-screen bg-white" data-testid="careers-page">
      <PageMeta
        title="Healthcare Staffing Careers & Clinician Opportunities | TRITAL Care®"
        description="Explore internal careers and clinician opportunities with TRITAL Care, a healthcare workforce solutions company supporting nursing, allied health, therapy, local, travel, contract, and permanent staffing."
        canonical="https://www.tritalcare.com/careers"
      />
      <PublicNav />

      <EasyApplyModal open={applyOpen} onOpenChange={setApplyOpen} role={selectedRole} />

      {/* 1 · HERO */}
      <section className="relative overflow-hidden bg-trital-mesh" data-testid="careers-hero">
        <div className="absolute inset-0 dot-grid opacity-60 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 pt-16 pb-20 lg:pt-20 lg:pb-24 grid lg:grid-cols-12 gap-10 items-center">
          <motion.div initial={{opacity:0,y:10}} animate={{opacity:1,y:0}} transition={{duration:0.5}} className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white border border-slate-200 shadow-card">
              <Sparkles className="w-3.5 h-3.5 text-trital-red" strokeWidth={1.8} />
              <span className="overline text-trital-navy">Careers at TRITAL Care®</span>
            </div>
            <h1 className="mt-6 font-display font-black text-5xl lg:text-[64px] leading-[1.02] tracking-tight text-slate-900 text-balance">
              Build the infrastructure
              <span className="block text-trital-navy">healthcare actually needs.</span>
            </h1>
            <p className="mt-6 text-slate-600 max-w-xl leading-relaxed text-lg">
              We hire operators who've staffed floors, engineers who've shipped enterprise software, and compliance leads who read HR.02 for sport. Remote-first across USA and India. Mission-driven, disciplined, direct.
            </p>
            <div className="mt-8 flex flex-wrap gap-3" data-testid="careers-hero-ctas">
              <Link
                to="/careers/jobs"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-trital-red text-white font-semibold btn-lift hover:bg-trital-red-deep"
                data-testid="careers-cta-view-jobs"
              >
                View Healthcare Jobs <ArrowRight className="w-4 h-4" strokeWidth={1.8} />
              </Link>
              <a
                href={CEIPAL_SUBMIT_RESUME_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-trital-navy text-white font-semibold btn-lift hover:bg-trital-navy-deep"
                data-testid="careers-cta-submit-resume"
              >
                Submit Your Resume <ArrowUpRight className="w-4 h-4" strokeWidth={1.8} />
              </a>
              <a
                href={`mailto:${RECRUITER_EMAIL}`}
                className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full border border-slate-300 text-trital-navy font-semibold hover:bg-slate-50"
                data-testid="careers-cta-contact-recruiting"
              >
                Contact Recruiting Team
              </a>
            </div>

            <div className="mt-6 flex flex-wrap gap-3">
              <a href="#roles" className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white border border-slate-200 text-trital-navy text-[13px] font-semibold hover:border-trital-navy" data-testid="careers-hero-see-roles">
                Or browse internal roles <ArrowRight className="w-3.5 h-3.5" strokeWidth={1.8} />
              </a>
              <a href="#values" className="inline-flex items-center px-5 py-2.5 rounded-full text-trital-navy text-[13px] font-semibold hover:underline" data-testid="careers-hero-values">
                How we work
              </a>
            </div>
          </motion.div>

          <motion.div initial={{opacity:0,scale:0.97}} animate={{opacity:1,scale:1}} transition={{duration:0.5}} className="lg:col-span-5">
            <div className="rounded-3xl border border-slate-200 bg-white shadow-navy p-6 lg:p-7 relative overflow-hidden">
              <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-trital-medical-blue-soft blur-3xl" />
              <div className="relative">
                <div className="overline text-trital-red">At a glance</div>
                <div className="mt-4 grid grid-cols-2 gap-3">
                  {[
                    { k: "Remote-first",    d: "USA + India timezones" },
                    { k: "3 offices",       d: "Missouri City · Stafford · Noida" },
                    { k: "Healthcare-only", d: "100% vertical focus" },
                    { k: "16 weeks",        d: "Paid parental leave" },
                  ].map(({k,d}) => (
                    <div key={k} className="rounded-xl border border-slate-200 bg-white p-3">
                      <div className="font-display font-black text-[15px] text-trital-navy leading-tight">{k}</div>
                      <div className="mt-1 text-[11.5px] text-slate-600 leading-snug">{d}</div>
                    </div>
                  ))}
                </div>
                <div className="mt-5 rounded-xl bg-slate-50 border border-slate-200 p-3 flex items-center gap-3">
                  <div className="w-9 h-9 rounded-lg bg-trital-navy text-white flex items-center justify-center"><Users className="w-4 h-4" strokeWidth={1.8}/></div>
                  <div className="text-[12.5px] text-slate-600 leading-snug">
                    Equal Opportunity Employer. We evaluate on craft and integrity — nothing else.
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 1b · CLINICIAN INTRO — bridge to the CEIPAL job board */}
      <section className="bg-slate-50 border-y border-slate-200" data-testid="careers-clinician-intro">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-16 lg:py-20 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="overline text-trital-red">For clinicians</div>
            <h2 className="mt-3 font-display font-black text-3xl lg:text-4xl tracking-tight text-slate-900 text-balance">
              Healthcare careers for nurses, allied & therapy clinicians.
            </h2>
            <p className="mt-4 text-slate-600 max-w-2xl leading-relaxed">
              TRITAL Care® is a healthcare workforce solutions company supporting{" "}
              <strong>nursing, allied health, therapy</strong>, and physician roles across{" "}
              <strong>local, travel, contract, and permanent</strong> placements. Our recruiting team works one-on-one with each clinician, with W-2 weekly pay, transparent rates, and full credentialing support.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link
                to="/careers/jobs"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-trital-red text-white text-[13.5px] font-semibold btn-lift hover:bg-trital-red-deep"
                data-testid="careers-intro-find-jobs"
              >
                View Healthcare Jobs <ArrowRight className="w-4 h-4" strokeWidth={1.8} />
              </Link>
              <a
                href={CEIPAL_SUBMIT_RESUME_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-white border border-slate-300 text-trital-navy text-[13.5px] font-semibold hover:border-trital-navy"
                data-testid="careers-intro-submit-resume"
              >
                Submit Your Resume <ArrowUpRight className="w-4 h-4" strokeWidth={1.8} />
              </a>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="rounded-3xl border border-slate-200 bg-white shadow-card p-6 lg:p-7">
              <div className="overline text-trital-navy">Specialties we place</div>
              <ul className="mt-4 grid grid-cols-2 gap-2 text-[13.5px] text-slate-700">
                <li className="flex items-center gap-2"><Stethoscope className="w-3.5 h-3.5 text-trital-red" strokeWidth={1.8}/> RN · LPN · CNA</li>
                <li className="flex items-center gap-2"><Stethoscope className="w-3.5 h-3.5 text-trital-red" strokeWidth={1.8}/> ICU · ER · OR · L&amp;D</li>
                <li className="flex items-center gap-2"><Stethoscope className="w-3.5 h-3.5 text-trital-red" strokeWidth={1.8}/> Therapy (PT, OT, SLP, RT)</li>
                <li className="flex items-center gap-2"><Stethoscope className="w-3.5 h-3.5 text-trital-red" strokeWidth={1.8}/> Allied (MA, MLT, Surg Tech)</li>
                <li className="flex items-center gap-2"><Stethoscope className="w-3.5 h-3.5 text-trital-red" strokeWidth={1.8}/> Physicians &amp; APPs</li>
                <li className="flex items-center gap-2"><Stethoscope className="w-3.5 h-3.5 text-trital-red" strokeWidth={1.8}/> Permanent · Travel · Local · Contract</li>
              </ul>
              <div className="mt-5 pt-5 border-t border-slate-200 text-[12.5px] text-slate-500">
                Looking for PRN shifts instead? Visit{" "}
                <Link to="/shiftbridge" className="text-trital-navy font-semibold underline underline-offset-2 hover:text-trital-red">
                  TRITAL ShiftBridge™
                </Link>
                {" "}— our W-2 PRN marketplace.
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 2 · VALUES / HOW WE WORK */}
      <section id="values" className="max-w-7xl mx-auto px-5 lg:px-8 py-24 scroll-mt-24" data-testid="careers-values">
        <div className="max-w-3xl">
          <div className="overline text-trital-red">How we work</div>
          <h2 className="mt-3 font-display font-black text-4xl lg:text-5xl tracking-tight text-slate-900 text-balance">
            Four operating values. Non-negotiable.
          </h2>
          <p className="mt-4 text-slate-600 max-w-2xl leading-relaxed">
            Not posters on a wall — these govern the hiring bar, the performance review rubric, and the moment-by-moment calls a senior person makes when nobody's watching.
          </p>
        </div>
        <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {VALUES.map(({Icon,t,d}, i) => (
            <motion.div
              key={t}
              initial={{opacity:0,y:10}} whileInView={{opacity:1,y:0}} viewport={{once:true,margin:"-60px"}}
              transition={{duration:0.35,delay:i*0.05}}
              className="rounded-2xl border border-slate-200 bg-white p-6 shadow-card hover:-translate-y-1 hover:shadow-card-hover transition-all"
              data-testid={`careers-value-${i}`}
            >
              <div className="w-11 h-11 rounded-xl bg-trital-medical-blue-soft text-trital-navy flex items-center justify-center">
                <Icon className="w-5 h-5" strokeWidth={1.8}/>
              </div>
              <div className="mt-5 font-display font-bold text-[17px] leading-snug text-slate-900">{t}</div>
              <p className="mt-2 text-[13.5px] text-slate-600 leading-relaxed">{d}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* 3 · BENEFITS */}
      <section className="bg-slate-50 border-y border-slate-200" data-testid="careers-benefits">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 py-24">
          <div className="grid lg:grid-cols-12 gap-10 items-end mb-10">
            <div className="lg:col-span-7">
              <div className="overline text-trital-red">Benefits</div>
              <h2 className="mt-3 font-display font-black text-4xl lg:text-5xl tracking-tight text-slate-900 text-balance">
                The floor we hold for everyone — every role.
              </h2>
            </div>
            <div className="lg:col-span-5">
              <p className="text-slate-600 leading-relaxed">
                No tier system between engineers and recruiters, between USA and India. Same health coverage, same learning stipend, same equity from day one.
              </p>
            </div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {BENEFITS.map(({Icon,t,d}, i) => (
              <motion.div
                key={t}
                initial={{opacity:0,y:10}} whileInView={{opacity:1,y:0}} viewport={{once:true,margin:"-60px"}}
                transition={{duration:0.35,delay:i*0.04}}
                className="rounded-2xl border border-slate-200 bg-white p-6 shadow-card"
                data-testid={`careers-benefit-${i}`}
              >
                <div className="w-11 h-11 rounded-xl bg-trital-navy text-white flex items-center justify-center">
                  <Icon className="w-5 h-5" strokeWidth={1.8}/>
                </div>
                <div className="mt-5 font-display font-bold text-[16px] text-slate-900">{t}</div>
                <p className="mt-2 text-[13px] text-slate-600 leading-relaxed">{d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 4 · OPEN ROLES */}
      <section id="roles" className="max-w-7xl mx-auto px-5 lg:px-8 py-24 scroll-mt-24" data-testid="careers-roles">
        <div className="grid lg:grid-cols-12 gap-10 items-end mb-10">
          <div className="lg:col-span-7">
            <div className="overline text-trital-red flex items-center gap-1.5">
              <Briefcase className="w-3 h-3" strokeWidth={1.8}/> Open roles
            </div>
            <h2 className="mt-3 font-display font-black text-4xl lg:text-5xl tracking-tight text-slate-900 text-balance">
              {ROLES.length} roles open across engineering, product, ops and go-to-market.
            </h2>
          </div>
          <div className="lg:col-span-5">
            <p className="text-slate-600 leading-relaxed">
              Don't see your role? Send a note to <a href="mailto:careers@tritalcare.com" className="text-trital-navy font-semibold hover:underline">careers@tritalcare.com</a> with what you'd build here — we read everything.
            </p>
          </div>
        </div>

        <ul className="rounded-2xl border border-slate-200 bg-white shadow-card divide-y divide-slate-100 overflow-hidden" data-testid="careers-roles-list">
          {ROLES.map((r, i) => (
            <motion.li
              key={r.t}
              initial={{opacity:0,y:6}} whileInView={{opacity:1,y:0}} viewport={{once:true,margin:"-40px"}}
              transition={{duration:0.3,delay:i*0.02}}
              data-testid={`careers-role-${i}`}
            >
              <div className="group flex flex-col md:flex-row md:items-center gap-4 px-5 lg:px-6 py-5 hover:bg-slate-50 transition-colors">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-trital-medical-blue-soft text-trital-navy text-[10px] font-bold uppercase tracking-wider">{r.tag}</span>
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 text-[10px] font-bold uppercase tracking-wider">{r.team} team</span>
                  </div>
                  <div className="mt-1.5 font-display font-bold text-[17px] text-slate-900 leading-tight">{r.t}</div>
                  <div className="mt-0.5 text-[12.5px] text-slate-500 inline-flex items-center gap-1.5">
                    <MapPin className="w-3.5 h-3.5" strokeWidth={1.8}/> {r.l}
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <a
                    href="https://www.linkedin.com/company/tritalcare/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full border border-[#0A66C2]/30 text-[#0A66C2] text-[12px] font-semibold hover:bg-[#0A66C2] hover:text-white transition-colors"
                    data-testid={`careers-role-${i}-linkedin`}
                    aria-label={`Apply via LinkedIn — ${r.t}`}
                  >
                    <Linkedin className="w-3.5 h-3.5" strokeWidth={1.8}/> LinkedIn
                  </a>
                  <button
                    type="button"
                    onClick={() => openApply(r)}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-trital-navy text-white text-[12.5px] font-semibold btn-lift hover:bg-trital-navy-deep"
                    data-testid={`careers-role-${i}-apply`}
                    aria-label={`Easy Apply — ${r.t}`}
                  >
                    Easy Apply <Send className="w-3.5 h-3.5" strokeWidth={1.8}/>
                  </button>
                </div>
              </div>
            </motion.li>
          ))}
        </ul>

        <div className="mt-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-slate-50 border border-slate-200 text-[12.5px] text-slate-600">
            <Sparkles className="w-3.5 h-3.5 text-trital-red" strokeWidth={1.8}/>
            We review every application within 5 business days — even the ones we can't hire today.
          </div>
          <button
            type="button"
            onClick={() => openApply({ t: "General application", l: "Remote · USA / India", tag: "Open", team: "Any" })}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full border border-slate-300 text-trital-navy text-[12.5px] font-semibold hover:bg-slate-50"
            data-testid="careers-general-apply"
          >
            Don't see a fit? Easy Apply anyway <ArrowUpRight className="w-4 h-4" strokeWidth={1.8}/>
          </button>
        </div>
      </section>

      {/* 5 · HIRING PROCESS */}
      <section className="bg-trital-navy-ink text-white relative overflow-hidden" data-testid="careers-process">
        <div className="absolute inset-0 bg-trital-navy-mesh opacity-85 pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 py-24">
          <div className="grid lg:grid-cols-12 gap-10 mb-10">
            <div className="lg:col-span-5">
              <div className="overline text-white/60">Our hiring process</div>
              <h2 className="mt-3 font-display font-black text-4xl lg:text-5xl tracking-tight text-white text-balance">
                No theatrics.
                <span className="block text-trital-red">Five steps. Two weeks.</span>
              </h2>
              <p className="mt-4 text-white/75 max-w-xl leading-relaxed">
                Our goal is to find out — together — whether this role will be the best work of your year. Fast decisions, clear feedback, no ghost loops.
              </p>
            </div>
            <ul className="lg:col-span-7 grid md:grid-cols-2 gap-4">
              {PROCESS.map(({n,t,d}) => (
                <li key={n} className="rounded-2xl border border-white/10 bg-white/5 backdrop-blur p-5" data-testid={`careers-process-${n}`}>
                  <div className="flex items-center gap-3">
                    <span className="w-9 h-9 rounded-full bg-white text-trital-navy font-display font-black flex items-center justify-center text-[13px]">{n}</span>
                    <span className="font-display font-bold text-[16px] text-white">{t}</span>
                  </div>
                  <p className="mt-3 text-[13px] text-white/75 leading-relaxed">{d}</p>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* 6 · LOCATIONS + CTA */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 py-24 grid lg:grid-cols-12 gap-8 items-start" data-testid="careers-locations">
        <div className="lg:col-span-5">
          <div className="overline text-trital-red">Where we work</div>
          <h2 className="mt-3 font-display font-black text-4xl lg:text-5xl tracking-tight text-slate-900 text-balance">
            Remote-first. 3 physical anchors.
          </h2>
          <p className="mt-4 text-slate-600 leading-relaxed max-w-lg">
            Most roles are fully remote. For hybrid-preference roles, we co-work from any of the three offices below. Quarterly team off-sites are on us.
          </p>
          <Link
            to="/contact"
            className="mt-6 inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-trital-navy text-white font-semibold btn-lift hover:bg-trital-navy-deep"
            data-testid="careers-locations-contact"
          >
            Questions? Talk to our team <ArrowRight className="w-4 h-4" strokeWidth={1.8}/>
          </Link>
        </div>
        <div className="lg:col-span-7 grid md:grid-cols-3 gap-4">
          {LOCATIONS.map(({label,lines}) => (
            <div key={label} className="rounded-2xl border border-slate-200 bg-white p-5 shadow-card" data-testid={`careers-location-${label.toLowerCase().replace(/[^a-z]+/g,'-').replace(/^-|-$/g,'')}`}>
              <div className="w-10 h-10 rounded-lg bg-trital-navy text-white flex items-center justify-center">
                <Building2 className="w-5 h-5" strokeWidth={1.8}/>
              </div>
              <div className="mt-4 overline text-slate-500">{label}</div>
              <div className="mt-1 text-[13.5px] text-slate-800 leading-snug">
                {lines.map((l) => <div key={l}>{l}</div>)}
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 lg:px-8 pb-16" data-testid="careers-sms">
        <SMSRecruiterCard variant="default" headline={SMS_COPY.careers.headline} subtext={SMS_COPY.careers.sub} />
      </section>

      <PublicFooter />
    </div>
  );
}
